import { lazy, ReactNode, Suspense } from "react";
import { Navigate, Route, Routes } from "react-router-dom";
import AuthWrapper from "../features/auth/AuthWrapper";
import ContentPageRouter from "../features/navigation/ContentPageRouter";
import MultisiteContentPageRouter from "../features/navigation/MultisiteContentPageRouter";
import SiteSelectionBoundary from "../features/navigation/SiteSelectionBoundary";
import { MultiSitePage, SitePage } from "./PageNames";
import SignedOut from "./SignedOut";

// Load each page when it is opened, rather than loading every product at startup.
const SiteGroupDashboard = lazy(() => import("../features/site_groups/SiteGroupDashboard"));
const ActivityLogs = lazy(() => import("./ActivityLogs"));
const Agriculture = lazy(() => import("./Agriculture"));
const Cameras = lazy(() => import("./Cameras"));
const Deployments = lazy(() => import("./Deployments"));
const Faults = lazy(() => import("./Faults"));
const History = lazy(() => import("./History"));
const HomeDashboard = lazy(() => import("./HomeDashboard"));
const LossAnalysis = lazy(() => import("./LossAnalysis"));
const InvertersPage = lazy(() => import("../features/inverters/InvertersPage"));
const MultiSite = lazy(() => import("./MultiSite"));
const Permissions = lazy(() => import("./Permissions"));
const AlertNotifications = lazy(
  () => import("../features/sites_configuration/alert_notifications/AlertsSubscribers")
);
const Reports = lazy(() => import("./Reports"));
const Settings = lazy(() => import("./Settings"));
const SiteConfiguration = lazy(() => import("./SiteConfiguration"));
const Sprinklers = lazy(() => import("./Sprinklers"));
const Trackers = lazy(() => import("./Trackers"));
const Trends = lazy(() => import("./Trends"));

export const AuthenticatedPageRoutes = () => {
  return (
    <SiteSelectionBoundary>
      <Suspense fallback={<p role="status">Loading page…</p>}>
        <Routes>
          <Route
            path="/SiteGroups"
            element={<MultisiteContentPageRouter content={<MultiSite siteGroupsActive />} />}
          />
          <Route path="/SiteGroups/:id" element={<SiteGroupDashboard />} />
          <Route
            path={`/:currentSite/${SitePage.Home}`}
            element={
              <ContentPageRouter
                backgroundWindowStyle={{ flex: 1, marginRight: "0px" }}
                layoutStyle={{ backgroundColor: "#ededed" }}
                scrollableLayout
                showShadowForScrollableLayout
              >
                <HomeDashboard />
              </ContentPageRouter>
            }
          />
          <Route
            path={`/:currentSite/${SitePage.Trackers}`}
            element={
              <ContentPageRouter>
                <Trackers />
              </ContentPageRouter>
            }
          />
          <Route
            path={`/:currentSite/${SitePage.History}`}
            element={
              <ContentPageRouter>
                <History />
              </ContentPageRouter>
            }
          />
          <Route
            path={`/:currentSite/${SitePage.History}/:historyTab`}
            element={
              <ContentPageRouter>
                <History />
              </ContentPageRouter>
            }
          />
          <Route
            path={`/:currentSite/${SitePage.Alerts}`}
            element={
              <ContentPageRouter>
                <Faults />
              </ContentPageRouter>
            }
          />
          <Route
            path={`/:currentSite/${SitePage.Agriculture}`}
            element={
              <ContentPageRouter>
                <Agriculture />
              </ContentPageRouter>
            }
          />
          <Route
            path={`/:currentSite/${SitePage.Configuration}`}
            element={
              <ContentPageRouter>
                <SiteConfiguration />
              </ContentPageRouter>
            }
          />
          <Route
            path={`/:currentSite/${SitePage.Faucets}`}
            element={
              <ContentPageRouter>
                <Sprinklers />
              </ContentPageRouter>
            }
          />
          <Route
            path={`/:currentSite/${SitePage.ActivityLog}`}
            element={
              <ContentPageRouter>
                <ActivityLogs />
              </ContentPageRouter>
            }
          />
          <Route
            path={`/:currentSite/${SitePage.Cameras}`}
            element={
              <ContentPageRouter
                backgroundWindowStyle={{ flex: 1, marginRight: "0px" }}
                layoutStyle={{ backgroundColor: "#ededed" }}
              >
                <Cameras />
              </ContentPageRouter>
            }
          />
          <Route
            path={`/:currentSite/${SitePage.Deployments}`}
            element={
              <ContentPageRouter>
                <Deployments />
              </ContentPageRouter>
            }
          />
          <Route
            path={`/:currentSite/${SitePage.LossAnalysis}`}
            element={
              <ContentPageRouter
                scrollableLayout
                layoutStyle={{ backgroundColor: "#ededed" }}
                backgroundWindowStyle={{ flex: 1, marginRight: "0px" }}
              >
                <LossAnalysis />
              </ContentPageRouter>
            }
          />
          <Route
            path={`/:currentSite/${SitePage.Reports}`}
            element={
              <ContentPageRouter
                scrollableLayout
                layoutStyle={{ backgroundColor: "#ededed" }}
                backgroundWindowStyle={{ flex: 1, marginRight: "0px" }}
              >
                <Reports />
              </ContentPageRouter>
            }
          />
          <Route
            path={`/:currentSite/${SitePage.Settings}`}
            element={
              <ContentPageRouter
                layoutStyle={{ backgroundColor: "#ededed" }}
                backgroundWindowStyle={{ flex: 1, marginRight: "0px" }}
              >
                <Settings />
              </ContentPageRouter>
            }
          />
          <Route
            path={`/:currentSite/${SitePage.Inverters}`}
            element={
              <ContentPageRouter
                layoutStyle={{ backgroundColor: "#ededed" }}
                backgroundWindowStyle={{ flex: 1, marginRight: "0px" }}
                scrollableLayout
                showShadowForScrollableLayout
              >
                <InvertersPage />
              </ContentPageRouter>
            }
          />
          <Route
            path={`/${MultiSitePage.Trends}`}
            element={<MultisiteContentPageRouter content={<Trends />} />}
          />
          <Route
            path={`/${MultiSitePage.Multisite}`}
            element={<MultisiteContentPageRouter content={<MultiSite />} />}
          />
          <Route
            path={`/${MultiSitePage.Permissions}`}
            element={<MultisiteContentPageRouter content={<Permissions />} />}
          />
          <Route
            path={`/${MultiSitePage.AlertNotifications}`}
            element={<MultisiteContentPageRouter content={<AlertNotifications />} />}
          />
          <Route path="/" element={<Navigate replace to={`/${MultiSitePage.Multisite}`} />} />
        </Routes>
      </Suspense>
    </SiteSelectionBoundary>
  );
};

interface IPageRoutesProps {
  protectedContent: ReactNode;
}

const PageRoutes = ({ protectedContent }: IPageRoutesProps) => (
  <Routes>
    <Route path="/signed-out" element={<SignedOut />} />
    <Route path="*" element={<AuthWrapper>{protectedContent}</AuthWrapper>} />
  </Routes>
);

export default PageRoutes;
